/**
 * Weekly class update templates for parent dashboard
 * Each template provides a fun, warm update about what happened in class
 */

export interface ClassUpdate {
  levelNumber: number;
  levelTitle: string;
  videoUrl: string; // YouTube embed URL
  activitySummary: string;
  learningHighlights: string[];
  conversationStarter: string; // Question for parents to ask their child
}

export const classUpdateTemplates: ClassUpdate[] = [
  {
    levelNumber: 1,
    levelTitle: "Rhythm Basics",
    videoUrl: "", // No video yet
    activitySummary: "{name} was clapping, stomping, and grooving to the beat! 🎉",
    learningHighlights: [
      "⭐ Earned {achievementCount} star{plural} for '{highlightedAchievement}'",
      "🤝 Learned alongside {teammates}",
      "🎵 Mastered quarter notes and eighth notes through movement"
    ],
    conversationStarter: "What is Overture Music Lab? Can you show me how to make a song with it?"
  },
  {
    levelNumber: 2,
    levelTitle: "Advanced Rhythm",
    videoUrl: "",
    activitySummary: "{name} became a beat-making wizard today! 🥁✨",
    learningHighlights: [
      "⭐ Unlocked {achievementCount} achievement{plural} for '{highlightedAchievement}'",
      "👥 Collaborated with {teammates}",
      "🥁 Created unique beats with digital tools"
    ],
    conversationStarter: "What did you make in Beat Decoders? Can you show me?"
  },
  {
    levelNumber: 3,
    levelTitle: "Harmony Basics",
    videoUrl: "",
    activitySummary: "{name} unlocked the secret feelings hidden in chords! 🎹💫",
    learningHighlights: [
      "⭐ Earned {achievementCount} star{plural} for '{highlightedAchievement}'",
      "🌟 Teamed with {teammates}",
      "🎹 Distinguished major vs. minor chords by ear"
    ],
    conversationStarter: "Did you enjoy playing Rocket Race and learning about major and minor chords? Does a happy song sound major or minor?"
  },
  {
    levelNumber: 4,
    levelTitle: "Advanced Harmony",
    videoUrl: "",
    activitySummary: "{name} crushed it in the Harmonidome challenge! 🎼🔥",
    learningHighlights: [
      "⭐ Earned {achievementCount} badge{plural} for '{highlightedAchievement}'",
      "💫 Competed with {teammates}",
      "🎼 Built chord sequences that tell emotional stories"
    ],
    conversationStarter: "What emotion did your Harmonidome chords create today - happy, exciting, or mysterious?"
  },
  {
    levelNumber: 5,
    levelTitle: "Instrument Families",
    videoUrl: "",
    activitySummary: "{name} became an instrument detective today! 🔍🎺",
    learningHighlights: [
      "⭐ Collected {achievementCount} achievement{plural} for '{highlightedAchievement}'",
      "🎸 Teamed with {teammates}",
      "🎺 Identified all four instrument families by sound"
    ],
    conversationStarter: "What's your favorite instrument family and why?"
  },
  {
    levelNumber: 6,
    levelTitle: "Instrumentation & Orchestration",
    videoUrl: "",
    activitySummary: "{name} learned to paint with sound like a composer! 🎨🎻",
    learningHighlights: [
      "⭐ Secured {achievementCount} honor{plural} for '{highlightedAchievement}'",
      "🤝 Challenged {teammates}",
      "🎻 Analyzed instrumental arrangements like a pro"
    ],
    conversationStarter: "Which instruments in Harmonidome and Rocket Race were your favorite?"
  },
  {
    levelNumber: 7,
    levelTitle: "Melody Basics",
    videoUrl: "",
    activitySummary: "{name} discovered the magic formula for catchy tunes! 🎶✨",
    learningHighlights: [
      "⭐ Earned {achievementCount} distinction{plural} for '{highlightedAchievement}'",
      "👯 Moved with {teammates}",
      "🎶 Mastered melodic contour and pitch direction"
    ],
    conversationStarter: "Can you show me how to play Melopedes?"
  },
  {
    levelNumber: 8,
    levelTitle: "Melody Composition",
    videoUrl: "",
    activitySummary: "{name} leveled up to melody composer! 🎵🚀",
    learningHighlights: [
      "⭐ Earned {achievementCount} composer badge{plural} for '{highlightedAchievement}'",
      "🎵 Challenged {teammates}",
      "✍️ Created melodies like a professional composer"
    ],
    conversationStarter: "What melody did you compose in Melopedes today? Can I listen to it with you?"
  },
  {
    levelNumber: 9,
    levelTitle: "Musical Form",
    videoUrl: "",
    activitySummary: "{name} became a song architect today! 🏗️🎵",
    learningHighlights: [
      "⭐ Unlocked {achievementCount} achievement{plural} for '{highlightedAchievement}'",
      "🎭 Teamed with {teammates}",
      "🏗️ Identified ABA form and verse-chorus structures"
    ],
    conversationStarter: "Does music have sections? Are memes a way to describe sections of a song?"
  },
  {
    levelNumber: 10,
    levelTitle: "Dynamics",
    videoUrl: "",
    activitySummary: "{name} mastered the power of volume today! 📢⚡",
    learningHighlights: [
      "⭐ Earned {achievementCount} badge{plural} for '{highlightedAchievement}'",
      "🌧️ Performed with {teammates}",
      "📢 Controlled dynamics from pp to ff"
    ],
    conversationStarter: "Would you like to show me how to play Flappy Muse?"
  },
  {
    levelNumber: 11,
    levelTitle: "Musical Genres",
    videoUrl: "",
    activitySummary: "{name} traveled the world through music today! 🌍🎶",
    learningHighlights: [
      "⭐ Earned {achievementCount} achievement{plural} for '{highlightedAchievement}'",
      "💃 Danced with {teammates}",
      "🌍 Identified unique elements of each genre"
    ],
    conversationStarter: "What music genre did you explore today? Can we listen to another song in that style?"
  },
  {
    levelNumber: 12,
    levelTitle: "Greatest Hits Showcase",
    videoUrl: "",
    activitySummary: "{name} rocked the showcase and leveled up! 🎊🎸",
    learningHighlights: [
      "⭐ Completed with {achievementCount} achievement{plural} for '{highlightedAchievement}'",
      "🤗 Celebrated with {teammates}",
      "🎊 Received certificates and reflected on their growth"
    ],
    conversationStarter: "Are you interested in learning more music next Season?"
  }
];

/**
 * Get class update for a specific level
 */
export function getClassUpdateForLevel(levelNumber: number): ClassUpdate | undefined {
  return classUpdateTemplates.find(template => template.levelNumber === levelNumber);
}

/**
 * Get specific activity name for collaboration based on level
 */
function getActivityForLevel(levelNumber: number): string {
  const activities: Record<number, string> = {
    1: 'Rhythm Rally',
    2: 'Beat Decoders',
    3: 'Yeet the Scoop',
    4: 'Harmonidome',
    5: 'Rocket Race',
    6: 'Harmonidome challenge',
    7: 'Step, Skip, or Repeat',
    8: 'Melopedes Composer',
    9: 'React Attack',
    10: 'Dynamics Rainstorm',
    11: 'Move to the Music',
    12: 'Greatest Hits Showcase'
  };
  return activities[levelNumber] || 'class activities';
}

/**
 * Get varied language for teammate interactions
 */
function getTeammateAction(levelNumber: number): string {
  const actions = [
    'jammed with',
    'collaborated with',
    'performed alongside',
    'competed with',
    'created music with',
    'rocked out with',
    'teamed up with',
    'grooved with'
  ];
  // Use level number as seed for consistent but varied language
  return actions[levelNumber % actions.length];
}

/**
 * Generate personalized class update text with student data
 * @param template The template to use
 * @param studentFirstName The student's first name (just first word of display name)
 * @param teammates Array of classmate first names (max 3)
 * @param stars Array of individual stars with reasons
 * @param levelNumber The level number for activity-specific language
 */
export function generatePersonalizedUpdate(
  template: ClassUpdate,
  studentFirstName: string,
  teammates: string[],
  stars: Array<{ reason: string; customReason?: string }>,
  levelNumber: number
): { summary: string; highlights: string[] } {
  // Use only 1-2 teammates for the highlight
  const selectedTeammates = teammates.slice(0, 2);
  const teammatesText = selectedTeammates.length > 0
    ? selectedTeammates.join(' and ')
    : 'their classmates';

  const summary = template.activitySummary
    .replace('{name}', studentFirstName);

  // Build highlights dynamically based on actual data
  const highlights: string[] = [];

  // Format stars with count and grouped reasons
  if (stars.length > 0) {
    const starCount = stars.length;
    const starPlural = starCount === 1 ? 'Gold Star' : 'Gold Stars';

    // Get all reasons, prioritizing custom reasons
    const reasons = stars.map(star => {
      if (star.customReason) {
        return star.customReason;
      }
      // Shorten standard reasons for the grouped format
      return star.reason.replace('Amazing ', '').replace('Great ', '').replace('Outstanding ', '');
    });

    const reasonsText = reasons.join(' & ');
    highlights.push(`⭐ Earned ${starCount} ${starPlural} (${reasonsText}!)`);
  }

  // Add teammates bullet with specific activity and varied language
  if (teammates.length > 0) {
    const activity = getActivityForLevel(levelNumber);
    const action = getTeammateAction(levelNumber);
    highlights.push(`🤝 ${action.charAt(0).toUpperCase() + action.slice(1)} ${teammatesText} in ${activity}`);
  }

  // Add the third learning highlight from the template (the specific skill)
  const skillHighlight = template.learningHighlights[2];
  if (skillHighlight) {
    highlights.push(skillHighlight);
  }

  return { summary, highlights };
}
